import { FormApi } from "final-form";
import React from "react";
import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "src/store";
import { PrimaryButton, SecondaryButton } from "src/ui/shared/CustomButton";
import { Modal } from "src/ui/shared/Modal/Modal";
import { makeStyles } from "tss-react/mui";

import { Group } from "@dashboard/devices/types";
import { selectGroups } from "@dashboard/groups/store/selectors";
import { Box, Theme } from "@mui/material";
import { CheckboxField } from "@shared/form/CheckboxField";

import { GroupWithChildren } from "./GroupFilterHeader";
import { applyHierarchyCheckboxToggle, buildGroupTree, getAllGroupIds } from "./GroupFilterHeader.utils";
import { GroupFilterItem } from "./GroupFilterItem";

const useStyles = makeStyles()((theme: Theme) => ({
    container: {
        margin: theme.spacing(3, 0),
        background: theme.palette.emteria.background.paper,
        borderRadius: theme.spacing(1),
        padding: theme.spacing(2),
    },
    content: {
        background: theme.palette.emteria.background.tableCells,
        borderRadius: theme.spacing(1),
        display: "flex",
        flexDirection: "column",
        position: "relative",
    },
}));

export type FilterTreeFormValues = Record<string, boolean>;

function hasExpandableGroups(groups: GroupWithChildren[]): boolean {
    return groups.some((group) => group.children.length > 0 || hasExpandableGroups(group.children));
}

function removeFirstCharFromKeys(obj: FilterTreeFormValues): FilterTreeFormValues {
    const newObj: FilterTreeFormValues = {};
    for (const key in obj) {
        newObj[key.slice(1)] = obj[key];
    }
    return newObj;
}
function createHierarchyToggle(
    allGroups: Group[],
    form: FormApi<FilterTreeFormValues>,
    isGroupDisabled?: (group: Group) => boolean,
) {
    return (group: GroupWithChildren) => {
        const values = form.getState().values as Record<string, boolean | undefined> | undefined;
        applyHierarchyCheckboxToggle(allGroups, group, values, (name, value) => form.change(name, value), isGroupDisabled);
    };
}

function getCheckedGroupIds(values: FilterTreeFormValues | undefined): number[] {
    return Object.entries(values ?? {})
        .filter(([, checked]) => checked)
        .map(([key]) => Number(key.slice(1)));
}

type Props = {
    open: boolean;
    initialValues: FilterTreeFormValues;
    onClose: () => void;
    onSubmit: (values: FilterTreeFormValues) => void | boolean | Promise<void | boolean>;
    title: string;
    description?: string;
    confirmLabel: string;
    confirmDisabled?: boolean;
    confirmLoading?: boolean;
    banner?: (checkedGroupIds: number[]) => React.ReactNode;
    isGroupDisabled?: (group: Group) => boolean;
    disabledTooltip?: string;
};

export const FilterTreeModal = (props: Props) => {
    const [expandedGroups, setExpandedGroups] = React.useState<number[]>([]);
    const { classes } = useStyles();
    const { t } = useTranslation(["common"]);

    const allGroups = useSelector(selectGroups);
    const groups = React.useMemo(() => buildGroupTree(allGroups), [allGroups]);
    const hasExpandable = React.useMemo(() => hasExpandableGroups(groups), [groups]);

    const onSubmit = async (values: FilterTreeFormValues, form: FormApi<FilterTreeFormValues>) => {
        const shouldClose = await props.onSubmit(removeFirstCharFromKeys(values));
        if (shouldClose !== false) {
            form.reset();
            props.onClose();
        }
    };

    const handleCloseButton = (form: FormApi<FilterTreeFormValues>) => {
        form.reset(props.initialValues);
        props.onClose();
    };

    const onExpand = (groupId: number) => {
        setExpandedGroups((prev) => (prev.includes(groupId) ? prev.filter((id) => id !== groupId) : [...prev, groupId]));
    };
    const onCollapseAllChange = () => {
        const allExpanded = groups.every((group) => expandedGroups.includes(group.id));
        setExpandedGroups(allExpanded ? [] : getAllGroupIds(groups));
    };
    const onSelectAll = (checked: boolean, form: FormApi<FilterTreeFormValues>) => {
        const currentValues = form.getState().values as FilterTreeFormValues | undefined;
        form.reset(
            Object.fromEntries(
                allGroups.map((group) => {
                    const key = `$${group.id}`;
                    const value = props.isGroupDisabled?.(group) ? Boolean(currentValues?.[key]) : checked;
                    return [key, value];
                }),
            ),
        );
    };

    return (
        <Form<FilterTreeFormValues>
            onSubmit={onSubmit}
            initialValues={props.initialValues}
            render={({ form, handleSubmit, values }) => (
                <form onSubmit={handleSubmit}>
                    <Modal
                        open={props.open}
                        title={props.title}
                        description={props.description}
                        onClose={() => handleCloseButton(form)}
                        endButton={
                            <PrimaryButton
                                onClick={form.submit}
                                disabled={props.confirmDisabled || props.confirmLoading}
                                loading={props.confirmLoading}
                            >
                                {props.confirmLabel}
                            </PrimaryButton>
                        }
                        startButton={
                            hasExpandable ? (
                                <SecondaryButton onClick={onCollapseAllChange}>
                                    {expandedGroups.length ? t("common:button.collapseAll") : t("common:button.expandAll")}
                                </SecondaryButton>
                            ) : null
                        }
                    >
                        {props.banner?.(getCheckedGroupIds(values))}
                        <Box className={classes.container}>
                            <Box className={classes.content}>
                                {groups.map((group) => (
                                    <GroupFilterItem
                                        key={group.id}
                                        group={group}
                                        expandedGroups={expandedGroups}
                                        onExpand={onExpand}
                                        onChange={(name, value) => form.change(name, value)}
                                        onToggleGroup={createHierarchyToggle(allGroups, form, props.isGroupDisabled)}
                                        isDisabled={props.isGroupDisabled}
                                        disabledTooltip={props.disabledTooltip}
                                    />
                                ))}
                            </Box>
                        </Box>

                        <CheckboxField
                            label={t("common:button.selectAll")}
                            checked={values && Object.values(values).every((item) => item)}
                            indeterminate={false}
                            onChange={(e, checked) => onSelectAll(checked, form)}
                        />
                    </Modal>
                </form>
            )}
        />
    );
};
